import "./NewsListPage.scss";
import {
  AriaStatus,
  GridCell,
  GridPresetTwoThirds,
  GridRow,
  HeroBasic,
  HideFor,
  LoadingOverlay,
  NewsListItem,
  Pagination,
  PublicationSearchPageFilters,
  PublicationSearchPageMobileFilters,
  RadioButton,
  SearchGlobalField,
  SortByRadioButton,
  ToastMessage,
} from "/App.Components";
import useListPageFilters, {
  BaseListFilters,
} from "/Helpers/Hooks/UseListPageFilters";
import { NewsListPageProps } from "/Models/Generated/NewsListPageProps";
import NewsListClient, { NewsListResults } from "./NewsListClient";
import { NewsListSectionProps } from "/Models/Generated/NewsListSectionProps";
import { useEffect, useRef, useState } from "react";
import useScrollTopTracker from "/Helpers/Hooks/useScrollTopTracker";
import useAriaUpdateHook from "/Helpers/Hooks/useAriaUpdateHook";
import { DropdownHierarchyProps, DropdownItemProps } from "/App.GeneratedTypes";
import { ReactSelectItem } from "/Helpers/ReactSelectItem";
import { GetDropdownHierarchyItem } from "/Helpers/dropdownItems";

// No value means no filter; the "All ..." items have no value, so don't look one up for it.
const getSelectedItem = (dropdown?: DropdownHierarchyProps, value?: string) =>
  value ? GetDropdownHierarchyItem(dropdown, value) : undefined;

export interface NewsListFilters extends BaseListFilters {
  pageNumber: number;
  searchTerm: string;
  area?: string;
  theme?: string;
  year?: number;
  fuzzySearch: boolean;
  sortByRelevance: boolean;
}

const NewsListPage = ({
  pageHeader,
  newsList,
  endpoint,
  language,
  pagination,
  baseUrl,
  areaDropdown,
  themeDropdown,
  yearDropdown,
  searchPlaceholder,
  searchButtonAriaLabel,
  searchResultFormatString,
  filtersHeading,
  filterSelectedAriaLiveSuffix,
  clearAllFiltersButtonText,
  sortByLabel,
  relevanceLabel,
  dateLabel,
  fuzzySearchLabel,
  searchTipsLabel,
  searchTips,
  initialSearchTerm,
  initialArea,
  initialTheme,
  initialYear,
  initialSortByRelevance,
  initialFuzzySearch,
}: NewsListPageProps) => {
  const client = new NewsListClient(
    endpoint,
    language,
    newsList?.newsListPageId,
    baseUrl,
  );
  const {
    items,
    resultsCount,
    error,
    loading,
    filters,
    totalPages,
    setError,
    setPageNumber,
    applyFilters,
  } = useListPageFilters({
    client: client,
    initList: newsList as NewsListResults,
    initFilters: {
      pageNumber: newsList?.currentPageNumber,
      searchTerm: initialSearchTerm ?? "",
      area: initialArea,
      theme: initialTheme,
      year: initialYear,
      fuzzySearch: initialFuzzySearch,
      sortByRelevance: initialSortByRelevance,
    },
  });

  const activeFilters = filters as NewsListFilters;
  // The search field is a draft until submitted; everything else lives in the hook's filters.
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? "");
  const [fuzzySearch, setFuzzySearch] = useState(initialFuzzySearch);
  const [autocompleteItems, setAutocompleteItems] = useState<ReactSelectItem[]>(
    [],
  );

  useEffect(() => {
    setSearchTerm(activeFilters.searchTerm);
    setFuzzySearch(activeFilters.fuzzySearch);
  }, [filters]);

  const [doScroll, setDoScroll] = useState(false);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const scrollTop = useScrollTopTracker(scrollToRef);

  useEffect(() => {
    if (loading) return;
    if (!doScroll) return;
    setDoScroll(false);
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [loading]);

  const { ariaStatus } = useAriaUpdateHook(resultsCount);

  const search = (filterChanges?: Partial<NewsListFilters>) =>
    applyFilters({ searchTerm, fuzzySearch, ...filterChanges });

  const sortBy = (sortByRelevance: boolean) => {
    if (sortByRelevance === activeFilters.sortByRelevance) return;
    applyFilters({ sortByRelevance });
  };

  const filterProps = {
    areaDropdown,
    themeDropdown,
    yearDropdown,
    selectedArea: getSelectedItem(areaDropdown, activeFilters.area),
    selectedTheme: getSelectedItem(themeDropdown, activeFilters.theme),
    selectedYear: getSelectedItem(yearDropdown, activeFilters.year?.toString()),
    setSelectedArea: (item?: DropdownItemProps) =>
      search({ area: item?.value }),
    setSelectedTheme: (item?: DropdownItemProps) =>
      search({ theme: item?.value }),
    setSelectedYear: (item?: DropdownItemProps) =>
      search({ year: item?.value ? Number(item.value) : undefined }),
    filterSelectedAriaLiveSuffix,
    clearAllFilters: () =>
      search({ area: undefined, theme: undefined, year: undefined }),
    clearAllFiltersText: clearAllFiltersButtonText ?? "",
  };

  return (
    <div className="news-list-page-norad">
      <ToastMessage
        message={error ?? ""}
        visible={!!error}
        onClose={() => setError("")}
      />
      <LoadingOverlay loading={loading} />
      {pageHeader && <HeroBasic {...pageHeader} />}
      <div className="news-list-page-norad__content" ref={scrollToRef}>
        <AriaStatus message={ariaStatus} />
        <GridRow>
          <GridPresetTwoThirds startColsDesktop={5}>
            <div className="news-list-page-norad__search">
              <SearchGlobalField
                id="news-list-search"
                size="medium"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                autocompleteItems={autocompleteItems}
                setAutocompleteItems={setAutocompleteItems}
                doAutocomplete={() => setAutocompleteItems([])}
                doSearch={(term) => search({ searchTerm: term })}
                searchButtonAriaLabel={searchButtonAriaLabel ?? ""}
                searchPlaceholder={searchPlaceholder ?? ""}
                fuzzySearch={fuzzySearch}
                setFuzzySearch={setFuzzySearch}
                fuzzySearchLabel={fuzzySearchLabel}
                searchTipsLabel={searchTipsLabel}
                searchTips={searchTips}
                noTopPadding
                noBottomPadding
              />
            </div>
          </GridPresetTwoThirds>
        </GridRow>
        <GridRow>
          <GridCell
            spanColsMobile={6}
            spanColsDesktop={4}
            spanColsDesktopLarge={3}
          >
            <HideFor mobile tablet>
              <aside className="news-list-page-norad__filters">
                {filtersHeading && <h2>{filtersHeading}</h2>}
                <PublicationSearchPageFilters {...filterProps} />
              </aside>
            </HideFor>
          </GridCell>
          <GridPresetTwoThirds startColsDesktop={5}>
            <div className="news-list-page-norad__result-header">
              <p className="news-list-page-norad__result-count">
                {searchResultFormatString?.replace(
                  "_",
                  `${resultsCount ?? newsList?.totalItems ?? 0}`,
                )}
              </p>
              {/* Mobile order per Figma: hit count, filters, sort. */}
              <HideFor desktop desktopMedium desktopLargeUp>
                <div className="news-list-page-norad__mobile-filters">
                  <PublicationSearchPageMobileFilters heading={filtersHeading}>
                    <PublicationSearchPageFilters {...filterProps} />
                  </PublicationSearchPageMobileFilters>
                </div>
              </HideFor>
              <SortByRadioButton description={sortByLabel}>
                <RadioButton
                  name="news-sort-by"
                  disabled={loading}
                  checked={activeFilters.sortByRelevance}
                  label={relevanceLabel ?? ""}
                  onClick={() => sortBy(true)}
                />
                <RadioButton
                  name="news-sort-by"
                  disabled={loading}
                  checked={!activeFilters.sortByRelevance}
                  label={dateLabel ?? ""}
                  onClick={() => sortBy(false)}
                />
              </SortByRadioButton>
            </div>
            {items && (
              <ol className="news-list-page-norad__list">
                {(items as NewsListSectionProps[]).map((section, idx) => (
                  <li
                    key={"section" + idx}
                    className="news-list-page-norad__section"
                  >
                    {section.sectionHeading && (
                      <h2 className="news-list-page-norad__section-heading">
                        {section.sectionHeading}
                      </h2>
                    )}
                    <ol className="news-list-page-norad__items">
                      {section.items?.map((item, itemIdx) => (
                        <NewsListItem key={"item" + itemIdx} {...item} />
                      ))}
                    </ol>
                  </li>
                ))}
              </ol>
            )}
            {items && (
              <div className="news-list-page-norad__pagination">
                <Pagination
                  {...pagination}
                  currentPage={activeFilters.pageNumber ?? 1}
                  setCurrentPage={(pageNum: number) => {
                    setPageNumber(pageNum);
                    setDoScroll(true);
                  }}
                  totalPages={totalPages ?? 1}
                  nextHrefForCrawlers={
                    (activeFilters.pageNumber ?? totalPages ?? 1) <
                    (totalPages ?? 1)
                      ? client.BuildUrlFromProps({
                          ...activeFilters,
                          pageNumber: (activeFilters.pageNumber ?? 1) + 1,
                        })
                      : undefined
                  }
                  previousHrefForCrawlers={
                    (activeFilters.pageNumber ?? 1) > 1
                      ? client.BuildUrlFromProps({
                          ...activeFilters,
                          pageNumber: (activeFilters.pageNumber ?? 1) - 1,
                        })
                      : undefined
                  }
                />
              </div>
            )}
          </GridPresetTwoThirds>
        </GridRow>
      </div>
    </div>
  );
};

export default NewsListPage;
